import type { BillboardView } from "@townsy/contracts";

/**
 * Which image URLs the web app will put in an `<img>` (ADR-0009 #2): approved derivatives are
 * served same-origin at `/api/v1/media/<key>.(png|webp)`; private previews (the owner's pending
 * upload, a reviewer's queue item) come from another authenticated same-origin API route. Nothing
 * from another origin, no `data:`/`blob:`/`javascript:` URLs, no path tricks. The renderer applies
 * its own allow-list as well.
 */

const MEDIA_RE = /^\/api\/v1\/media\/[A-Za-z0-9][A-Za-z0-9_-]{0,127}\.(png|webp)$/;
const API_PATH_RE = /^\/api\/v1\/[A-Za-z0-9/_.-]{1,400}(\?[A-Za-z0-9=&_.%-]{0,200})?$/;

/** A public, approved derivative (`/api/v1/media/<key>.png|webp`). */
export function isApprovedMediaUrl(url: string | null | undefined): url is string {
  return typeof url === "string" && MEDIA_RE.test(url);
}

/** A same-origin API path (approved media or an authenticated private preview route). */
export function isSameOriginApiImage(url: string | null | undefined): url is string {
  if (typeof url !== "string" || !API_PATH_RE.test(url)) return false;
  // No traversal or protocol-relative tricks.
  return !url.includes("..") && !url.includes("//");
}

/** "Billboard 1" for `bb-1` when the server's label is not at hand. */
export function billboardName(billboardId: string, label?: string | null): string {
  if (label && label.trim()) return label.trim();
  const m = /^bb-(\d{1,2})$/.exec(billboardId);
  return m ? `Billboard ${Number(m[1])}` : billboardId;
}

/**
 * What the renderer gets (`scene.setBillboards`): the server's list, with any image URL that is
 * not an approved same-origin derivative dropped, so the board falls back to the text creative.
 */
export function billboardsForScene(items: readonly BillboardView[]): BillboardView[] {
  return items.map((b) =>
    b.current && b.current.imageUrl !== null && !isApprovedMediaUrl(b.current.imageUrl) ? { ...b, current: { ...b.current, imageUrl: null } } : b,
  );
}

/** Stable identity of what the boards show, so the renderer is only called when something changed. */
export function billboardsKey(items: readonly BillboardView[]): string {
  return items
    .map((b) => `${b.billboardId}:${b.current ? `${b.current.creativeId}|${b.current.imageUrl ?? ""}|${b.current.until}` : "-"}:${b.dayRateCents}`)
    .join(",");
}
